import React from "react";
import { useEffect, useState } from "react";
import logger from "@/utils/logger";
import FlagIcon from "@/components/icons/flag-icon";
import TrackIcon from "@/components/icons/track-icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MarketStartTime from "./market-start-time";

const TrackRaceSelector = React.memo(
  function TrackRaceSelector({
    selectedTrack,
    selectedRace,
    tracks,
    races,
    onTrackChange,
    onRaceChange,
    isTrackSelectOpen,
    onTrackSelectOpenChange,
    variant = "default", // "default" | "results"
    className = "",
    hasHistoricalData,
    marketStartTime,
    hasTips = false,
  }) {
    const isDefaultVariant = variant === "default";
    const shouldShowMarketTime = marketStartTime && hasTips;
    // logger.log("shouldShowMarketTime: ", shouldShowMarketTime, ", hasTips: ", hasTips, ", marketStartTime: ", marketStartTime);

    if (isDefaultVariant) {
      return (
        <div className="top-pink-bar fixed top-0 left-0 right-0 max-w-md mx-auto flex justify-between items-center mb-2 px-0 mt-0 align-middle bg-deepPink h-[35px] z-[1000]">
          <div
            className={`track-race-selector w-full gap-0 mb-0 px-0 z-25 relative font-semibold ${className}`}
          >
            <div
              className={`items-center grid ${shouldShowMarketTime ? "grid-cols-[1fr_minmax(auto,60px)_minmax(auto,60px)]" : "grid-cols-[1fr_minmax(auto,60px)]"} gap-0 pl-3 pr-3`}
            >
              <div className="select-track-container relative">
                <Select
                  value={selectedTrack}
                  onValueChange={onTrackChange}
                  open={isTrackSelectOpen}
                  onOpenChange={onTrackSelectOpenChange}
                  className=""
                >
                  <SelectTrigger className="bg-transparent text-white rounded-none border-none">
                    <div className="flex items-center gap-2 w-full overflow-hidden">
                      <TrackIcon />
                      <SelectValue
                        placeholder="Select Track"
                        className="overflow-hidden text-ellipsis whitespace-nowrap"
                      />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {tracks?.map((track) => (
                      <SelectItem
                        key={track}
                        value={track}
                        className="overflow-hidden text-ellipsis whitespace-nowrap max-w-[150px]"
                      >
                        <div className="flex items-center gap-2 w-full overflow-hidden">
                          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {track}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="select-race-container pl-2">
                <Select value={selectedRace} onValueChange={onRaceChange}>
                  <SelectTrigger className="bg-transparent text-white rounded-none border-none">
                    <div className="flex items-center gap-2">
                      <FlagIcon />
                      <SelectValue placeholder="Select Race" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {races?.map((race) => (
                      <SelectItem key={race} value={race.toString()}>
                        <div className="flex items-center gap-2">{race}</div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {shouldShowMarketTime && (
                <div className="race-start-time-container h-9 relative text-right pl-0 pr-0 py-0 type-0">
                  <MarketStartTime
                    marketStartTime={marketStartTime}
                    hasHistoricalData={hasHistoricalData}
                    variant={variant}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Results variant
    return (
      <div
        className={`track-race-selector grid grid-cols-[2fr_1fr] gap-4 mb-6 ${className}`}
      >
        <div className="select-track-container">
          <p className="text-sm text-[#76313C] mb-2">Track</p>
          <Select
            value={selectedTrack}
            onValueChange={onTrackChange}
            open={isTrackSelectOpen}
            onOpenChange={onTrackSelectOpenChange}
          >
            <SelectTrigger className="bg-white shadow-md">
              <div className="flex items-center gap-2 w-full overflow-hidden">
                <TrackIcon />
                <SelectValue
                  placeholder="Select Track"
                  className="overflow-hidden text-ellipsis whitespace-nowrap"
                />
              </div>
            </SelectTrigger>
            <SelectContent>
              {tracks?.map((track) => (
                <SelectItem key={track} value={track}>
                  <div className="flex items-center gap-2">{track}</div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="select-race-container">
          <p className="text-sm text-[#76313C] mb-2">Race</p>
          <Select value={selectedRace} onValueChange={onRaceChange}>
            <SelectTrigger className="bg-white shadow-md">
              <div className="flex items-center gap-2">
                <FlagIcon />
                <SelectValue placeholder="Select Race" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {races?.map((race) => (
                <SelectItem key={race} value={race.toString()}>
                  <div className="flex items-center gap-2">{race}</div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* <div className="market-start-time-container">
        <p className="text-sm text-[#76313C] mb-2">Start Time</p>
        {marketStartTime && (
          <MarketStartTime
            marketStartTime={marketStartTime}
            hasHistoricalData={hasHistoricalData}
            variant={variant}
          />
        )}
      </div> */}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.selectedTrack === nextProps.selectedTrack &&
      prevProps.selectedRace === nextProps.selectedRace &&
      prevProps.marketStartTime === nextProps.marketStartTime &&
      prevProps.hasTips === nextProps.hasTips &&
      prevProps.hasHistoricalData === nextProps.hasHistoricalData &&
      prevProps.variant === nextProps.variant &&
      prevProps.className === nextProps.className &&
      prevProps.isTrackSelectOpen === nextProps.isTrackSelectOpen &&
      JSON.stringify(prevProps.tracks) === JSON.stringify(nextProps.tracks) &&
      JSON.stringify(prevProps.races) === JSON.stringify(nextProps.races)
    );
  },
);

export default TrackRaceSelector;
